import 'primeicons/primeicons.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.css';
import React, { useEffect, useMemo } from 'react';
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth"
import { useLayout } from "../../hooks/useLayout";
import { useLocalStorage } from "../../helpers/useLocalStorage";

import { SECTION_ORDER, get_nav_items } from "./navConfig";
import { useFeatures } from "../../hooks/useFeatures";
import LogoWhite from "../../assets/images/logo_exaloan_long_white_l.png";
// Hexagon mark on its own (cropped from the white wordmark) for the collapsed rail.
import LogoMark from "../../assets/images/exaloan_mark_white.png";
import "./sidebar.css";

const initials_of = (user) => {
    if (!user) {
        return "?";
    }
    const name = String(user).split("@")[0];
    const parts = name.split(/[._-]+/).filter(Boolean);
    return ((parts[0] || "")[0] + ((parts[1] || "")[0] || "")).toUpperCase() || "?";
};

function Sidebar() {
    const { user, groups, platform, is_viewing_as } = useAuth();
    const features = useFeatures();
    const { setNavOpen } = useLayout();
    const location = useLocation();
    const [collapsed_sections, setCollapsedSections] = useLocalStorage("sidebar_collapsed_sections", {});

    const nav_menu_items = useMemo(
        () => get_nav_items(groups, platform, is_viewing_as, features),
        [groups, platform, is_viewing_as, features]
    );

    // The mobile drawer closes itself on every navigation, so picking a page
    // doesn't leave the nav covering the page just opened.
    useEffect(() => {
        setNavOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const sections = SECTION_ORDER
        .map(section => ({ section, items: nav_menu_items.filter(item => item.section === section) }))
        .filter(({ items }) => items.length > 0);
    const ungrouped_items = nav_menu_items.filter(item => !item.section);

    const render_nav_item = item => (
        <NavLink
            key={item.id}
            to={item.link_to}
            end={item.link_to === "/"}
            data-label={item.text}
            className={(navData) => (navData.isActive ? "sb-link is-active" : "sb-link")}
        >
            <i className={`pi ${item.icon} sb-link-icon`} />
            <span className="sb-link-text">{item.text}</span>
        </NavLink>
    );

    // Groups a section's items by `subsection` (e.g. Reporting's "Risk
    // Analytics Report" vs "Compartment Report"), preserving each group's
    // first-seen order - an item with no `subsection` stays on its own,
    // un-headered.
    const render_section_items = (items) => {
        const item_groups = [];
        const bySubsection = new Map();
        for (const item of items) {
            if (!item.subsection) {
                item_groups.push({ subsection: null, items: [item] });
                continue;
            }
            if (!bySubsection.has(item.subsection)) {
                const group = { subsection: item.subsection, items: [] };
                bySubsection.set(item.subsection, group);
                item_groups.push(group);
            }
            bySubsection.get(item.subsection).items.push(item);
        }
        return item_groups.map((group, i) => (
            <div className="sb-subsection" key={group.subsection || `plain-${i}`}>
                {group.subsection && <div className="sb-subsection-label">{group.subsection}</div>}
                {group.items.map(render_nav_item)}
            </div>
        ));
    };

    const toggle_section = (section) =>
        setCollapsedSections({ ...collapsed_sections, [section]: !collapsed_sections[section] });

    return (
        <div id="sidebar-container" className="sb">
            <div className="sb-brand">
                <img alt="Exaloan" className="sb-brand-logo" src={LogoWhite} />
                <img alt="Exaloan" className="sb-brand-monogram" src={LogoMark} />
                <span className="sb-brand-product">ExaPort</span>
            </div>

            <nav id="sidebar" className="sb-scroll">
                <div className="sb-toplevel">
                    {ungrouped_items.map(render_nav_item)}
                </div>

                {sections.map(({ section, items }) => {
                    const is_section_collapsed = !!collapsed_sections[section];
                    return (
                        <div key={section} className={`sb-section${is_section_collapsed ? " is-collapsed" : ""}`}>
                            <button
                                type="button"
                                className="sb-section-header"
                                aria-expanded={!is_section_collapsed}
                                onClick={() => toggle_section(section)}
                            >
                                <span>{section}</span>
                                <i className="pi pi-chevron-down sb-section-chevron" />
                            </button>
                            <div className="sb-section-body">
                                {render_section_items(items)}
                            </div>
                        </div>
                    );
                })}
            </nav>

            <div className="sb-footer">
                {user && (
                    <div className="sb-user" title={user}>
                        <span className="sb-avatar">{initials_of(user)}</span>
                        <span className="sb-user-meta">
                            <span className="sb-user-name">{user}</span>
                            {platform && <span className="sb-user-platform">{platform}</span>}
                        </span>
                    </div>
                )}
                <div className="sb-legal">Exaloan AG &copy; {new Date().getFullYear()}</div>
            </div>
        </div>
    );
};

export default Sidebar;
